// Copyright (c) Ping. All rights reserved.
//
// TypeScript shapes for AppEvent bodies, mirroring the byte-level
// contract in `docs/architecture/types-and-schemas.md` §2.2 – §2.14.
// Only the body shapes the chat surface actually renders today live
// here; the others are documented in the spec but unused by the UI
// until their feature lands.
//
// Wire-format note: the spec mandates deterministic CBOR with
// integer-keyed maps (§1.1). The current ping-prime client encodes
// the same logical envelope as a JSON wrapper
// (`{ kind: 'com.ping.X', payload: {...} }`) — see the kind ↔
// AppEventType map in `./kinds.ts`. The types below are written
// against the doc shape so a future CBOR-migration is a renderer-
// less swap; today's JSON parser in `./envelope.ts` adapts payloads
// onto these interfaces.

// -----------------------------------------------------------------
// AppEventType — numeric tag registry (§2.1)
// -----------------------------------------------------------------

export const ContentType = {
  TEXT: 0x10,
  IMAGE: 0x11,
  VIDEO: 0x12,
  VOICE: 0x13,
  FILE: 0x14,
  LOCATION_ONCE: 0x15,
  LOCATION_BEACON_START: 0x16,
  LOCATION_BEACON_TICK: 0x17,
  LOCATION_BEACON_STOP: 0x18,
  STICKER: 0x19,
  LINK_PREVIEW: 0x1a,
} as const;

export const MutationType = {
  EDIT: 0x20,
  REDACT: 0x21,
  FORWARD: 0x22,
} as const;

export const AffordanceType = {
  REACTION: 0x30,
  REACTION_REMOVE: 0x31,
  THREAD_ROOT: 0x33,
} as const;

export const GroupStateType = {
  GROUP_NAME: 0x40,
  GROUP_AVATAR: 0x41,
  GROUP_TOPIC: 0x42,
  JOIN_RULE: 0x43,
  HISTORY_VISIBILITY: 0x44,
  POWER_LEVELS: 0x45,
} as const;

export const MemberType = {
  INVITE: 0x50,
  KNOCK: 0x51,
  KICK_REASON: 0x52,
  BAN: 0x53,
  ROOM_UPGRADE: 0x54,
  CONTACT_REQUEST: 0x55,
  CONTACT_REQUEST_RESPONSE: 0x56,
  // Personal-group member role. DRIFT: personal groups originally had flat MLS
  // membership; this E2E-synced event records a member's group role (owner /
  // admin / member) so every member sees the same roster authority. See the
  // DRIFT note in docs/architecture/protocol.md + types-and-schemas.md.
  MEMBER_ROLE_SET: 0x57,
} as const;

export const PinningType = {
  PIN: 0x60,
  UNPIN: 0x61,
  // Personal "saved messages" bookmark (per-conversation, synced to the
  // saver's own devices via the same path as pin).
  SAVE: 0x62,
  UNSAVE: 0x63,
  // Personal "starred messages" (DeviceGroup-only). Additive live payload
  // `{ target, starred }` (no UNSTAR kind — `starred: false` unstars).
  STAR: 0x64,
} as const;

export const PollType = {
  POLL_CREATE: 0x70,
  POLL_VOTE: 0x71,
} as const;

export const GameType = {
  GAME_CREATE: 0x80,
  GAME_JOIN: 0x81,
  GAME_MOVE: 0x82,
  GAME_REVEAL: 0x83,
  GAME_OUTCOME: 0x84,
  GAME_ABORT: 0x85,
} as const;

export const PersonalType = {
  NOTE_UPSERT: 0x90,
  NOTE_DELETE: 0x91,
  TASK_UPSERT: 0x92,
  TASK_DELETE: 0x93,
  // Activity reminders ("remind me about this") — DeviceGroup-only so a
  // reminder set on one device follows the user to all their devices.
  // types-and-schemas §2.10.
  REMINDER_SET: 0x94,
  REMINDER_CLEAR: 0x95,
  // Cross-device "product tour seen" mirror (DeviceGroup-only): finishing a
  // guided tour on one device suppresses it on the user's other devices.
  TOUR_SEEN: 0x96,
  READ_POINTER: 0x9a,
  // Self-profile change nudge (DeviceGroup-only): tells the account's other
  // devices to re-pull `/v1/me/profile` after a name/avatar edit (multi-device
  // realtime profile sync). Carries no payload — server is the source of truth.
  SELF_PROFILE: 0x9b,
  // Per-conversation personal state patch (mute/archive/pin/favorite/delete/
  // folder), mirrored across the user's own devices over the DeviceGroup.
  CONV_STATE: 0x9c,
  // Personal block list (DeviceGroup-only; server-blind). types-and-schemas §2.10.
  BLOCKED_USER_UPSERT: 0x9d,
  BLOCKED_USER_REMOVE: 0x9e,
} as const;

export const CallType = {
  CALL_INVITE: 0xa0,
  CALL_ACCEPT: 0xa1,
  CALL_DECLINE: 0xa2,
  CALL_END: 0xa3,
  CALL_RINGING: 0xa4,
  CALL_HAND_RAISE: 0xa5,
  CALL_REACTION: 0xa6,
  // Ephemeral live-roster presence ping — broadcast on each participant
  // join/leave so members NOT in the call see a live count/avatars on the
  // ongoing call tile. Consumed by the call-signaling feed; never persisted
  // or rendered as a chat bubble. Client-encoded JSON (the numeric tag is only
  // the CBOR-migration mirror; the wire bytes carry the string `kind`).
  CALL_ROSTER: 0xa7,
  CALL_HANDOFF: 0xa8,
} as const;

export const ScheduledType = {
  // Disappearing-messages policy. types-and-schemas §2.13.
  DISAPPEARING_POLICY: 0xc0,
  MEETING_CREATE: 0xc2,
  MEETING_UPDATE: 0xc3,
  MEETING_RSVP: 0xc4,
  MEETING_CANCEL: 0xc5,
} as const;

// Union of all numeric tags we actively render. The full registry is
// `number`; this alias documents the subset the variant + preview
// dispatch handles today.
export type AppEventType = number;

// -----------------------------------------------------------------
// Body shapes (§2.2)
// -----------------------------------------------------------------

export interface TextRange {
  readonly start: number;
  readonly end: number;
}

export interface Mention {
  /**
   * Logical user identifier. The doc specifies 16-byte UUID bytes;
   * the JSON wrapper carries the hex-encoded account id as a string,
   * so we keep both shapes available for forward compatibility.
   */
  readonly userId: string;
  readonly range: TextRange;
  /**
   * Optional pre-resolved display name surfaced by the sender's
   * directory snapshot — lets the receiver render an `@Alice` chip
   * without a directory round-trip.
   */
  readonly displayName?: string | undefined;
}

export interface CodeBlock {
  readonly range: TextRange;
  readonly language?: string | undefined;
}

export interface TextBody {
  readonly body: string;
  readonly mentions?: ReadonlyArray<Mention> | undefined;
  readonly format?: 'plain' | 'rich' | undefined;
  readonly spoilers?: ReadonlyArray<TextRange> | undefined;
  readonly code_blocks?: ReadonlyArray<CodeBlock> | undefined;
}

/**
 * Wire form of a blob reference. The doc specifies four `Uint8Array`
 * fields (media_id 32, content_key 32, content_iv 12, hash 32). The
 * JSON wrapper today encodes the bytes inline (base64) — fields are
 * left as `string` here so both forms compile.
 */
export interface BlobRef {
  readonly media_id?: string | undefined;
  readonly content_key?: string | undefined;
  readonly content_iv?: string | undefined;
  readonly hash?: string | undefined;
  /**
   * Already-resolved local URL (object-URL, data-URL, or remote
   * https URL). Set by the host when the blob is materialized; the
   * tile renderers use this directly when present.
   */
  readonly url?: string | undefined;
  /**
   * Inline bytes carried as base64 in the JSON wrapper (used by the
   * current voice + image upload paths until the full blob store is
   * wired). When set, callers can decode without a separate fetch.
   */
  readonly bytes?: string | undefined;
}

export interface ImageBody {
  readonly blob_ref: BlobRef;
  readonly caption?: string | undefined;
  readonly w: number;
  readonly h: number;
  readonly mime: string;
  readonly blurhash?: string | undefined;
  readonly thumb_ref?: BlobRef | undefined;
  /**
   * True when a `com.ping.gif` envelope is actually a STICKER (payload
   * `sticker: true`). Carried through so the tile renders chrome-free (no
   * bubble background). Only ever set by `decodeGif`; plain images never set it.
   */
  readonly sticker?: boolean | undefined;
}

export interface VideoBody {
  readonly blob_ref: BlobRef;
  readonly caption?: string | undefined;
  readonly w: number;
  readonly h: number;
  readonly dur_ms: number;
  readonly mime: string;
  readonly poster_ref?: BlobRef | undefined;
}

export interface VoiceBody {
  readonly blob_ref: BlobRef;
  readonly dur_ms: number;
  /** Amplitude samples 0..255, length 64 per the doc. */
  readonly waveform: ReadonlyArray<number>;
}

export interface FileBody {
  readonly blob_ref: BlobRef;
  readonly name: string;
  readonly size: number;
  readonly mime: string;
  /** Optional preview metadata for PDFs / images so the doc tile can
   *  show the first-page chip without re-fetching the blob. */
  readonly page_count?: number | undefined;
}

export interface LocationOnceBody {
  readonly lat: number;
  readonly lon: number;
  readonly accuracy_m: number;
  readonly label?: string | undefined;
}

export interface StickerBody {
  readonly pack_id: string;
  readonly sticker_id: string;
  readonly fallback_blob_ref?: BlobRef | undefined;
}

export interface LinkPreviewBody {
  readonly url: string;
  readonly title: string;
  readonly desc?: string | undefined;
  readonly image_ref?: BlobRef | undefined;
}

// -----------------------------------------------------------------
// Mutation shapes (§2.3)
// -----------------------------------------------------------------

export interface EditBody {
  /** UUID hex of the event being edited. */
  readonly target: string;
  readonly new_type: number;
  /**
   * Spec is CBOR-bytes of the new body. The JSON wrapper today nests
   * the new body shape directly — `new_text` is the convenience
   * accessor used by the preview formatter when `new_type === TEXT`.
   */
  readonly new_text?: string | undefined;
  readonly new_body?: unknown;
}

export interface RedactBody {
  readonly target: string;
  readonly reason?: 'user' | 'moderation' | 'retention' | undefined;
}

export interface ForwardBody {
  readonly source_event_id: string;
  readonly source_conversation_id: string;
  readonly original_sender?: string | undefined;
  readonly content_type: number;
  /**
   * The forwarded inner body. Decoded eagerly by `decodeApplicationEnvelope`
   * so callers can recursively format a preview without a second
   * decode pass.
   */
  readonly content_body?: unknown;
}

// -----------------------------------------------------------------
// Affordance shapes (§2.4)
// -----------------------------------------------------------------

export interface ReactionBody {
  readonly target: string;
  readonly emoji: string;
}

export interface ReactionRemoveBody {
  readonly target: string;
  readonly emoji: string;
}

export interface ThreadRootBody {
  readonly from: string;
}

// -----------------------------------------------------------------
// Group state shapes (§2.5)
// -----------------------------------------------------------------

export interface GroupNameBody {
  readonly name: string;
  readonly actorDisplayName?: string | undefined;
  /** Stable group-avatar media id, ridden on the rename/hydration broadcast so a
   *  web joiner (who can't read the GroupContext meta on their Welcome) converges
   *  the group PICTURE from the same reliably-delivered event that sets the name —
   *  instead of depending on a separate `com.ping.group_avatar` that can be
   *  independently dropped. `groupNamePayloadToConversationPatch` reads it. */
  readonly avatarMediaId?: string | undefined;
  /** Full authoritative roster (account ids) at broadcast time, union-merged on
   *  receipt (never shrinks) — belt-and-braces convergence, mirroring
   *  `com.ping.invite`, so a member that missed the invite/add broadcast still
   *  converges the member list off the rename event. */
  readonly memberAccountIds?: readonly string[] | undefined;
  /** account_id → display name for the roster above (instant name render). */
  readonly memberDisplayNames?: Readonly<Record<string, string>> | undefined;
}

export interface GroupAvatarBody {
  readonly blob_ref: BlobRef;
  readonly actorDisplayName?: string | undefined;
}

export interface GroupTopicBody {
  readonly topic: string;
  readonly actorDisplayName?: string | undefined;
}

// -----------------------------------------------------------------
// Member shapes (§2.6)
// -----------------------------------------------------------------

export interface InviteBody {
  readonly invitee: string;
  readonly invite_kind: 'user' | 'link' | 'alias';
  readonly expires_at?: number | undefined;
  readonly actorDisplayName?: string | undefined;
  readonly inviteeDisplayName?: string | undefined;
  /** The invited member's 16-byte directory account id. Carried so the SAME
   *  event that reliably renders "X invited Y" ALSO updates every recipient's
   *  roster (`memberAccountIds`) directly — instead of depending on a SEPARATE
   *  `com.ping.group_name` broadcast that can be independently dropped, which is
   *  how two members ended up showing different member counts. */
  readonly inviteeAccountId?: string | undefined;
  /** The full authoritative post-add roster (peer account ids, minus self) at the
   *  time of the invite. Lets the invite event converge the WHOLE member list on
   *  receipt (union-merged), not just the one invitee — belt-and-braces against a
   *  missed group_name broadcast. */
  readonly memberAccountIds?: readonly string[] | undefined;
  /** account_id → display name for the roster above (for instant name render). */
  readonly memberDisplayNames?: Readonly<Record<string, string>> | undefined;
}

export interface KickReasonBody {
  readonly target_user_id: string;
  readonly reason: string;
  readonly actorDisplayName?: string | undefined;
  readonly targetDisplayName?: string | undefined;
}

// -----------------------------------------------------------------
// Pinning shapes (§2.7)
// -----------------------------------------------------------------

export interface PinBody {
  readonly target: string;
  readonly actorDisplayName?: string | undefined;
}

export interface UnpinBody {
  readonly target: string;
  readonly actorDisplayName?: string | undefined;
}

// Saved-messages bookmark (§2.7 extension). `target` is the saved message's
// content hash, mirroring pin.
export interface SaveBody {
  readonly target: string;
}
export interface UnsaveBody {
  readonly target: string;
}

// Personal block list (§2.10). DeviceGroup-only.
export interface BlockedUserUpsertBody {
  readonly blocked_user_id: string;
  readonly blocked_at: number;
}
export interface BlockedUserRemoveBody {
  readonly blocked_user_id: string;
}

// Disappearing-messages policy (§2.13).
export interface DisappearingPolicyBody {
  readonly scope: 'conversation' | 'event';
  readonly ttl_seconds: number;
  readonly target_event_id?: string | undefined;
}

// Personal-group member role (drift — see MemberType.MEMBER_ROLE_SET).
export type GroupMemberRole = 'owner' | 'admin' | 'member';
export interface MemberRoleSetBody {
  readonly target_user_id: string;
  readonly role: GroupMemberRole;
  readonly actorDisplayName?: string | undefined;
}

// -----------------------------------------------------------------
// Polls (§2.8) — preview only; full UI deferred.
// -----------------------------------------------------------------

export interface PollCreateBody {
  readonly question: string;
  readonly options: ReadonlyArray<string>;
  readonly multi_select: boolean;
  readonly anonymous: boolean;
  readonly closes_at?: number | undefined;
}

/**
 * §2.8 `0x71 POLL_VOTE` — a sidecar vote on an existing poll (never a
 * chat bubble; see `deriveVariant`). The doc body is
 * `{ poll: Uint8Array, choices: number[] }`; the JSON wrapper today
 * carries the single-choice OR-set shape the UI projects:
 *   - `target` — the poll's content hash (the `poll_create` message's
 *     contentHash), the join key the vote OR-set is anchored on. Aliased
 *     `poll`/`poll_id` on decode for forward-compat with the CBOR doc
 *     shape.
 *   - `option_index` — index into the poll's `options` array.
 *   - `actorAccountId` — the voter's accountId; defaults to the envelope
 *     sender on ingest when omitted.
 *   - `choices` — the actor's FULL selected set for a MULTI-select poll
 *     (the OR-set of option indices they currently hold). Single-choice
 *     senders omit it and carry only `option_index`; multi-select senders
 *     ship the whole set so the receiver can REPLACE the actor's prior
 *     rows with it. `option_index` stays populated (= `choices[0]`) for
 *     back-compat with single-choice consumers.
 * One vote per actor per option: single-choice is latest-wins on a single
 * row; multi-select is an OR-set REPLACE keyed on `(actor, choices)`.
 */
export interface PollVoteBody {
  readonly target: string;
  readonly option_index: number;
  readonly choices?: readonly number[] | undefined;
  readonly actorAccountId?: string | undefined;
}

// -----------------------------------------------------------------
// Call signaling (§2.11) — preview + header CTA only.
// -----------------------------------------------------------------

export interface CallInviteBody {
  readonly call_id: string;
  readonly call_group_id: string;
  readonly media: 'audio' | 'video';
  readonly initiator_device: string;
  readonly actorDisplayName?: string | undefined;
  /** True when this is a group (huddle) call — drives grid layout + the
   * group hangup rule (stays active while ≥1 peer remains) on the callee. */
  readonly group?: boolean | undefined;
}

export interface CallEndBody {
  readonly call_id: string;
  readonly reason: string;
  readonly duration_ms?: number | undefined;
  /**
   * ACCOUNT ids of everyone who joined the call (union observed by the terminal
   * `call_end` emitter). Drives the persisted participant avatar stack for ALL
   * viewers (bystanders / post-call / reload) and per-viewer "missed" when the
   * local account is absent. Optional + additive — absent on legacy events and
   * from clients predating the field. See
   * `docs/architecture/calls-participant-roster-spec.md` (pending SDK release).
   */
  readonly participants?: readonly string[] | undefined;
}

// One callee device accepts. Other devices dismiss on receipt
// (first-accept-wins, resolved by server_seq). calls.md §6.
export interface CallAcceptBody {
  readonly call_id: string;
  readonly call_group_id: string;
  readonly accepting_device: string;
}

// Explicit decline, timeout, or busy. Stops the ring on all callee
// devices. `reason` ∈ declined|timeout|busy.
export interface CallDeclineBody {
  readonly call_id: string;
  readonly reason: 'declined' | 'timeout' | 'busy';
}

// Optional ack that a callee device received the invite ("Ringing…"
// state on the caller side). calls.md §5.3.10 (0xA4).
export interface CallRingingBody {
  readonly call_id: string;
}

// In-call hand-raise toggle. Rendered as an overlay on the raiser's
// tile. Rides the host conversation in v1 (call-ephemeral is a
// follow-up). calls.md §11.
export interface CallHandRaiseBody {
  readonly call_id: string;
  readonly raised: boolean;
}

// In-call flying-emoji reaction. calls.md §11.
export interface CallReactionBody {
  readonly call_id: string;
  readonly emoji: string;
}

// Ephemeral live-roster presence ping (CallType.CALL_ROSTER). Broadcast on each
// participant join/leave so members NOT in the call see a live count/avatars on
// the ongoing call tile. Never persisted; consumed by the call-signaling feed.
export interface CallRosterBody {
  readonly call_id: string;
  // ACCOUNT ids currently in the call (self included). Empty is valid (last
  // participant about to leave).
  readonly participants: ReadonlyArray<string>;
}

// Cross-device handoff: "continue this call on another device". Sent on the
// host conversation; the initiator's other devices (matching `account`,
// excluding `from_device`) prompt to take over. calls.md §16.
export interface CallHandoffBody {
  readonly call_id: string;
  readonly call_group_id: string;
  readonly media: 'audio' | 'video';
  readonly account: string;
  readonly from_device: string;
}

// -----------------------------------------------------------------
// Games (§2.9, GameType 0x80–0x85)
// -----------------------------------------------------------------
//
// Wire-format note: the doc shapes carry the binary fields
// (`session_id`, `payload`, `salt`, `value`, `final_state`, `winner`,
// per-payout `user_id`) as `Uint8Array`. The JSON wrapper today
// encodes those as hex strings on the wire — the interfaces below use
// `string` so both the current JSON path and a future CBOR swap
// compile against the same shape. `session_id` is the stable join key
// the game host keys its session state on.

export type GameKind =
  | 'coinflip'
  | 'dice'
  | 'rps'
  | 'chess'
  | 'blackjack'
  | 'racing';

export interface GameStakes {
  readonly kind: 'leaderboard' | 'none';
}

export interface GameCreateBody {
  readonly game_kind: GameKind;
  /** Hex-encoded session id (16-byte UUID on the wire). */
  readonly session_id: string;
  /** Game-kind-specific creation params (board size, rounds, etc.). */
  readonly params: Record<string, unknown>;
  readonly stakes?: GameStakes | undefined;
}

export interface GameJoinBody {
  readonly session_id: string;
  readonly seat: number;
}

export interface GameMoveBody {
  readonly session_id: string;
  readonly move_idx: number;
  /** Game-kind-specific move encoding (hex on the wire). */
  readonly payload: string;
  /** SHA-256(salt || move) for hidden-state games (hex). */
  readonly commit?: string | undefined;
}

export interface GameRevealBody {
  readonly session_id: string;
  readonly move_idx: number;
  /** Commitment salt (hex). */
  readonly salt: string;
  /** The original committed move (hex). */
  readonly value: string;
}

export interface GamePayout {
  /** Hex-encoded user id. */
  readonly user_id: string;
  readonly points: number;
}

export interface GameOutcomeBody {
  readonly session_id: string;
  /** Winning user id (hex), omitted for a tie. */
  readonly winner?: string | undefined;
  readonly payouts?: ReadonlyArray<GamePayout> | undefined;
  /** Final serialized game state (hex). */
  readonly final_state: string;
}

export interface GameAbortBody {
  readonly session_id: string;
  readonly reason: 'timeout' | 'leave' | 'invalid_state';
}

// -----------------------------------------------------------------
// Meetings (§2.13, 0xC2–0xC5). Cross-platform wire — MUST match iOS
// `MeetingAppEvent.swift` byte-for-byte (snake_case keys; optional
// fields OMITTED, never JSON null; ids are 32-char lowercase hex;
// timestamps are ms epoch). Only MEETING_CREATE renders a timeline card;
// update/rsvp/cancel are sidecars that mutate the materialized state.
// -----------------------------------------------------------------

/** Optional call linkage: the "Ping call" composer mode. The card shows a
 *  Join button while the meeting is in progress; it never auto-launches. */
export interface MeetingAutoCall {
  readonly media: 'audio' | 'video';
  readonly pre_invite_minutes: number;
}

export interface MeetingCreateBody {
  readonly meeting_id: string;
  readonly title: string;
  readonly starts_at: number;
  readonly ends_at: number;
  readonly location?: string | undefined;
  readonly attendee_user_ids: ReadonlyArray<string>;
  readonly description?: string | undefined;
  readonly auto_call?: MeetingAutoCall | undefined;
  /** Additive web field (forward-compatible; iOS drops unknown keys today):
   *  the Figma "Meeting link" URL, rendered as Open/Copy link on the card.
   *  Distinct from freeform `location`. */
  readonly meeting_link?: string | undefined;
}

/** Creator-only partial diff. Absent field = unchanged; empty-string
 *  `location`/`description`/`meeting_link` clears it. */
export interface MeetingUpdateBody {
  readonly meeting_id: string;
  readonly title?: string | undefined;
  readonly starts_at?: number | undefined;
  readonly ends_at?: number | undefined;
  readonly location?: string | undefined;
  readonly description?: string | undefined;
  readonly meeting_link?: string | undefined;
  readonly attendee_user_ids_added?: ReadonlyArray<string> | undefined;
  readonly attendee_user_ids_removed?: ReadonlyArray<string> | undefined;
}

export type MeetingRsvpResponse = 'yes' | 'no' | 'maybe';

/** LWW per (meeting_id, voter). Voter identity is the MLS-authenticated
 *  sender, never the payload. */
export interface MeetingRsvpBody {
  readonly meeting_id: string;
  readonly response: MeetingRsvpResponse;
  readonly note?: string | undefined;
}

/** Creator-only and absorbing: once applied, no later event resurrects. */
export interface MeetingCancelBody {
  readonly meeting_id: string;
  readonly reason?: string | undefined;
}

// -----------------------------------------------------------------
// Personal state (§2.10, PersonalType 0x90–0x9F) — DeviceGroup-only.
// -----------------------------------------------------------------
//
// Wire-format note: the doc body shapes carry the id fields
// (`note_id`, `task_id`) as `Uint8Array` (16-byte UUID). The JSON
// wrapper today encodes those as hex strings on the wire — the
// interfaces below use `string` so the current JSON path and a future
// CBOR swap compile against the same shape. These events are only
// valid inside the user's own DeviceGroup conversation and are
// rejected by reducers in any external conversation.

export interface NoteUpsertBody {
  /** Hex-encoded note id (16-byte UUID on the wire). Stable per note. */
  readonly note_id: string;
  readonly title: string;
  readonly body: string;
  readonly tags?: ReadonlyArray<string> | undefined;
  /** Wall-clock ms of the last edit; last-writer-wins across devices. */
  readonly updated_at: number;
}

export interface NoteDeleteBody {
  readonly note_id: string;
}

export interface TaskUpsertBody {
  /** Hex-encoded task id (16-byte UUID on the wire). Stable per task. */
  readonly task_id: string;
  readonly title: string;
  /** Optional due date (wall-clock ms). */
  readonly due?: number | undefined;
  readonly done?: boolean | undefined;
  /** Optional list / bucket name the task belongs to. */
  readonly list?: string | undefined;
  /** Fractional index, lex-sortable — drives manual ordering. */
  readonly position: string;
  // Richer additive fields (live drift, §2.10). Kept in sync with the
  // `deviceGroupTaskSync` sender + `useIncomingMessages` apply path + iOS, so
  // the decode surface no longer silently narrows a task's detail.
  readonly priority?: 'low' | 'medium' | 'high' | 'urgent' | undefined;
  readonly state?:
    | 'backlog'
    | 'pending'
    | 'inProgress'
    | 'completed'
    | 'cancelled'
    | undefined;
  readonly assignee_id?: string | undefined;
  readonly description?: string | undefined;
  /** Exact cross-device LWW key (ms). */
  readonly updated_at?: number | undefined;
}

export interface TaskDeleteBody {
  readonly task_id: string;
}

/** DeviceGroup-only nudge to re-fetch the shared profile. No payload — the
 *  server (`/v1/me/profile`) is authoritative. */
export type SelfProfileBody = Record<string, never>;

/** DeviceGroup-only per-conversation personal-state patch (mute/archive/pin/
 *  favorite/delete/folder) mirrored across the user's own devices. */
export interface ConvStateBody {
  /** Target conversation (hex id) the patch applies to. */
  readonly conversationId: string;
  /** Field/value pairs to apply locally on each sibling device. */
  readonly patch: Record<string, unknown>;
}

/** DeviceGroup-only per-conversation read pointer. Mirrors the user's
 *  read position across their own devices so the Activity unread state
 *  (and chat unread badges) stay consistent. Last-writer-wins on
 *  `last_read_at`. types-and-schemas §2.10. */
export interface ReadPointerBody {
  /** Target conversation (hex id). */
  readonly conversation_id: string;
  /** Hex message id read up to, when known. */
  readonly last_read_message_id?: string | undefined;
  /** Wall-clock ms of the newest message considered read. */
  readonly last_read_at: number;
}

/** DeviceGroup-only "remind me about this" reminder attached to an
 *  activity / message. Synced so the reminder follows the user across
 *  devices; any device may fire the local notification at `fire_at`.
 *  types-and-schemas §2.10. */
export interface ReminderSetBody {
  /** Hex-encoded reminder id (16-byte UUID on the wire). Stable per reminder. */
  readonly reminder_id: string;
  /** Conversation (hex id) the reminded item lives in. */
  readonly conversation_id: string;
  /** Hex message id the reminder anchors to, when applicable. */
  readonly message_id?: string | undefined;
  /** Wall-clock ms when the reminder should fire. */
  readonly fire_at: number;
  /** Optional free-text note attached to the reminder. */
  readonly note?: string | undefined;
  /** Wall-clock ms the reminder was created; last-writer-wins across devices. */
  readonly created_at: number;
}

export interface ReminderClearBody {
  readonly reminder_id: string;
}

/** DeviceGroup-only "product tour seen" mirror. Finishing a guided tour on one
 *  device broadcasts this so the user's other devices don't re-run it.
 *  Last-writer-wins on seen_at. types-and-schemas §2.10. */
export interface TourSeenBody {
  readonly tour_id: string;
  readonly seen_at: number;
}
